import React from "react";
import {Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField} from "@mui/material";
import type {DropRequest} from "~/prototypes/pages/taxonomy-canvas/useTaxonomyDrag.ts";

type Props = {
    request: DropRequest | null
    /** For a merge, `name` is the name of the merged category */
    onConfirm: (name: string) => void
    onCancel: () => void
}

const Name: React.FC<{children: React.ReactNode}> = ({children}) =>
    <Box component="strong" sx={{color: 'text.primary'}}>{children}</Box>;

/** Confirms a merge or move made by dragging one category onto another */
export const TaxonomyDropDialog: React.FC<Props> = ({request, onConfirm, onCancel}) => {
    const isMerge = request?.action === 'merge';
    const n = request?.source.childCount ?? 0;
    // Merged name starts as the target's name; reset whenever a new drop is confirmed
    const [name, setName] = React.useState('');
    const [prevRequest, setPrevRequest] = React.useState(request);
    if (prevRequest !== request) {
        setPrevRequest(request);
        setName(request?.target.label ?? '');
    }
    const canConfirm = !isMerge || name.trim().length > 0;
    const confirm = () => {
        if (canConfirm) onConfirm(name.trim());
    };
    return <Dialog open={request !== null} onClose={onCancel} maxWidth="xs" fullWidth>
        <DialogTitle>{isMerge ? 'Merge categories' : 'Move category'}</DialogTitle>
        {request && <DialogContent>
            <DialogContentText>
                {isMerge
                    ? <>Do you want to merge <Name>{request.source.label}</Name> into <Name>{request.target.label}</Name>?</>
                    : <>Do you want to move <Name>{request.source.label}</Name> under <Name>{request.target.label}</Name>?</>}
            </DialogContentText>
            {isMerge && <DialogContentText sx={{mt: 1.5}}>
                <Name>{request.source.label}</Name> will be removed and its spend added to <Name>{request.target.label}</Name>
                {n > 0 && <>, together with its {n} {n === 1 ? 'sub-category' : 'sub-categories'}</>}.
            </DialogContentText>}
            {isMerge && <TextField
                label="Name of the merged category"
                value={name}
                onChange={e => setName(e.target.value)}
                onKeyDown={e => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        confirm();
                    }
                }}
                error={!canConfirm}
                helperText={canConfirm ? undefined : 'Enter a name'}
                size="small"
                fullWidth
                autoFocus
                onFocus={e => e.target.select()}
                sx={{mt: 2.5}}
            />}
            {!isMerge && n > 0 && <DialogContentText sx={{mt: 1.5}}>
                Its {n} {n === 1 ? 'sub-category moves' : 'sub-categories move'} with it.
            </DialogContentText>}
        </DialogContent>}
        <DialogActions>
            <Button onClick={onCancel}>Cancel</Button>
            <Button variant="contained" onClick={confirm} disabled={!canConfirm} autoFocus={!isMerge}>
                {isMerge ? 'Merge' : 'Move'}
            </Button>
        </DialogActions>
    </Dialog>
}
